import React, { useState, useEffect } from 'react';

interface SensorData {
  sensor_id: string;
  sensor_type: string;
  status: 'healthy' | 'flatline' | 'faulty' | 'offline';
  last_reading: string | null;
  last_value: number | null;
  flatline_duration: number;
}

interface BlockHealth {
  [blockId: string]: SensorData[];
}

interface HealthSummary {
  total_sensors: number;
  healthy_sensors: number;
  faulty_sensors: number;
  health_percentage: number;
}

interface SensorHealthBadgeProps {
  plantId: string;
}

const STATUS_COLORS = {
  healthy: 'bg-green-100 text-green-800 border-green-200',
  flatline: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  faulty: 'bg-red-100 text-red-800 border-red-200',
  offline: 'bg-gray-100 text-gray-800 border-gray-200'
};

const STATUS_ICONS = {
  healthy: '✓',
  flatline: '⚠',
  faulty: '✗',
  offline: '○'
};

export const SensorHealthBadge: React.FC<SensorHealthBadgeProps> = ({ plantId }) => {
  const [healthData, setHealthData] = useState<BlockHealth>({});
  const [summary, setSummary] = useState<HealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchSensorHealth();
  }, [plantId]);

  const fetchSensorHealth = async () => {
    try {
      setLoading(true);
      
      // Mock sensor health data
      const mockBlocks: { [key: string]: any[] } = {};
      for (let i = 1; i <= 20; i++) {
        const blockId = `BLOCK-${i.toString().padStart(2, '0')}`;
        mockBlocks[blockId] = Array.from({length: 3}, (_, j) => ({
          sensor_id: `TEMP_${blockId}_${(j + 1).toString().padStart(2, '0')}`,
          sensor_type: 'temperature',
          status: Math.random() > 0.15 ? 'healthy' : ['flatline', 'faulty', 'offline'][Math.floor(Math.random() * 3)],
          last_reading: new Date().toISOString(),
          last_value: 20 + Math.random() * 25,
          flatline_duration: Math.random() > 0.8 ? Math.floor(Math.random() * 120) : 0
        }));
      }
      
      const totalSensors = 60;
      const healthySensors = Object.values(mockBlocks).flat().filter(s => s.status === 'healthy').length;
      
      setHealthData(mockBlocks);
      setSummary({
        total_sensors: totalSensors,
        healthy_sensors: healthySensors,
        faulty_sensors: totalSensors - healthySensors,
        health_percentage: Math.round((healthySensors / totalSensors) * 100 * 10) / 10
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const toggleBlockExpansion = (blockId: string) => {
    const newExpanded = new Set(expandedBlocks);
    if (newExpanded.has(blockId)) {
      newExpanded.delete(blockId);
    } else {
      newExpanded.add(blockId);
    }
    setExpandedBlocks(newExpanded);
  };

  const getBlockHealthSummary = (sensors: SensorData[]) => {
    const healthy = sensors.filter(s => s.status === 'healthy').length;
    const total = sensors.length;
    return { healthy, total, percentage: (healthy / total) * 100 };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600 text-sm">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Sensor Health Monitor
        </h3>
        
        {/* Overall Summary */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{summary.total_sensors}</div>
              <div className="text-sm text-gray-600">Total Sensors</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{summary.healthy_sensors}</div>
              <div className="text-sm text-gray-600">Healthy</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{summary.faulty_sensors}</div>
              <div className="text-sm text-gray-600">Issues</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{summary.health_percentage}%</div>
              <div className="text-sm text-gray-600">Health Score</div>
            </div>
          </div>
        )}
      </div>

      {/* Block-wise Health Status */}
      <div className="space-y-3">
        {Object.entries(healthData).map(([blockId, sensors]) => {
          const blockHealth = getBlockHealthSummary(sensors);
          const isExpanded = expandedBlocks.has(blockId);
          
          return (
            <div key={blockId} className="border rounded-lg">
              <button
                onClick={() => toggleBlockExpansion(blockId)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="font-medium text-gray-900">{blockId}</span>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      blockHealth.percentage >= 90 ? 'bg-green-100 text-green-800' :
                      blockHealth.percentage >= 70 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {blockHealth.healthy}/{blockHealth.total} Healthy
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {blockHealth.percentage.toFixed(0)}%
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-3 border-t bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                    {sensors.map((sensor) => (
                      <div
                        key={sensor.sensor_id}
                        className={`p-3 rounded border ${STATUS_COLORS[sensor.status]}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{sensor.sensor_id}</span>
                          <span className="text-lg">{STATUS_ICONS[sensor.status]}</span>
                        </div>
                        <div className="text-xs space-y-1">
                          <div>Type: {sensor.sensor_type}</div>
                          <div>Status: {sensor.status}</div>
                          {sensor.last_value && (
                            <div>Last: {sensor.last_value.toFixed(1)}°C</div>
                          )}
                          {sensor.status === 'flatline' && sensor.flatline_duration > 0 && (
                            <div>Flatline: {sensor.flatline_duration}min</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Refresh Button */}
      <div className="mt-4 flex justify-end">
        <button
          onClick={fetchSensorHealth}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Refresh Status
        </button>
      </div>
    </div>
  );
};